"use client";
import React from "react";
import { PageManifestoSchema } from "@/sanity/pages/PageManifestoTemplate";
import NavBarV2 from "@/components/global/partials/HeaderV2";
import Footer from "@/components/global/partials/Footer";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableText } from "next-sanity";
import Image from "@/helpers/Image";
import chart from "@/public/manifesto/chart.svg";
import statue from "@/public/manifesto/image.png";
import problemBg from "@/public/manifesto/problemBg.png";
import { motion } from "framer-motion";

export default function ProblemSection({
  data,
}: {
  data: PageManifestoSchema;
}) {
  return (
    <>
      <section id="problem">
        <div className="w-[min(94.25rem,100%)] mx-auto flex flex-col gap-[4rem]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0,
              ease: [0.25, 0.4, 0.25, 1],
            }}
            viewport={{ once: true }}
            className="relative overflow-clip bg-[#FFE2E2] rounded-[3rem] px-[2.75rem] lg:px-[4rem] py-[4.75rem] lg:min-h-[39rem]"
          >
            <Image
              src={problemBg}
              alt=""
              className="absolute top-0 right-0 z-0 !h-full !w-[60rem] lg:!w-[58.125rem]"
            />
            <Image
              src={chart}
              alt=""
              className="absolute !w-[90%] bottom-0 lg:bottom-[-2rem] right-0 z-0 mix-blend-plus-lighter opacity-[0.15]"
            />
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{
                duration: 3,
                delay: 0,
                ease: [0.25, 0.4, 0.25, 1],
              }}
              viewport={{ once: true }}
              className="absolute bottom-0 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-0 z-0 !w-[30rem] lg:!w-[39.625rem]"
            >
              <Image src={statue} alt="" />
            </motion.div>

            <div className="min-h-[40rem] lg:min-h-[29.5rem] relative z-[1] flex flex-col lg:justify-between gap-[2rem]">
              {data?.problemTitle && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: 0,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  viewport={{ once: true }}
                  className="richtext richtext-start w-full
                  prose-h2:text-[4rem] lg:prose-h2:text-[4.5rem] prose-h2:mb-[3rem]
                  prose-img:!w-[6.7rem] prose-img:my-0
                  prose-h4:text-[2.375rem] prose-h4:text-[#103037] prose-h4:mt-0
                  prose-h5:text-[2.25rem] prose-h5:text-[#103037]
                  prose-hr:border-black/10
                  [&_.font-gestura]:text-[3rem] [&_.font-gestura]:text-[#07171B] [&_.font-gestura]:font-bold [&_.font-gestura]:tracking-[-0.06rem] [&_.font-gestura]:leading-[112%]
                  [&_.faded]:!text-[#07171B]/50"
                >
                  <PortableText
                    value={data.problemTitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
              {data?.problemSubtitle && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: 0.4,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}
                  viewport={{ once: true }}
                  className="richtext richtext-start lg:w-[48.75rem]
                  prose-p:text-[2rem] prose-strong:font-normal prose-strong:text-black"
                >
                  <PortableText
                    value={data.problemSubtitle}
                    components={richTextComponent}
                  />
                </motion.div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0,
              ease: [0.25, 0.4, 0.25, 1],
            }}
            viewport={{ once: true }}
            className="px-[2rem] lg:px-[7rem]"
          >
            {data?.problemContent && (
              <div
                className="richtext richtext-start gap-[2rem]
                prose-p:my-0 prose-h4:my-0
                prose-strong:font-normal prose-strong:text-black"
              >
                <PortableText
                  value={data.problemContent}
                  components={richTextComponent}
                />
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
}
